import * as THREE from 'three';

// ============================================================
// ParticleSystem -- lightweight GPU-based particle emitter
// using THREE.Points with additive blending
// ============================================================

export interface ParticleConfig {
  /** Maximum pool size (pre-allocated) */
  maxParticles: number;
  /** Default lifetime in seconds */
  lifetime: number;
  /** Initial velocity magnitude */
  speed: number;
  /** Random spread angle (radians) applied to the emission direction */
  spread: number;
  /** GL point size */
  size: number;
  /** Base color (supports HDR values > 1.0 for bloom pickup) */
  color: THREE.Color;
  /** Whether particles fade out over their lifetime */
  fadeOut: boolean;
  /** Downward acceleration (positive = gravity pulls down) */
  gravity: number;
  /**
   * Billboard particle shape.
   *
   * - 'circle' (default) -- soft radial glow, general purpose
   * - 'star'             -- 4-point star burst, ideal for item pickups
   * - 'fire'             -- teardrop flame shape, ideal for boost particles
   */
  shape?: 'circle' | 'star' | 'fire';
}

/** CPU-side per-particle state */
interface Particle {
  vx: number;
  vy: number;
  vz: number;
  age: number;
  lifetime: number;
}

/**
 * A reusable, pool-based particle system rendered with THREE.Points.
 *
 * Particles are stored in a ring buffer. Dead particles have their positions
 * moved far offscreen (y = -9999) so the GPU still draws them but they are
 * invisible without needing to compact the buffer every frame.
 *
 * All attributes use DynamicDrawUsage for efficient streaming updates.
 */
export class ParticleSystem {
  private points: THREE.Points;
  private geometry: THREE.BufferGeometry;
  private material: THREE.ShaderMaterial;

  // Typed-array buffers (backing the BufferAttributes)
  private posArr: Float32Array;
  private alphaArr: Float32Array;

  // BufferAttribute handles (for needsUpdate)
  private posAttr: THREE.BufferAttribute;
  private alphaAttr: THREE.BufferAttribute;

  // CPU-side particle pool
  private particles: (Particle | null)[];
  private nextIndex: number = 0;

  private config: ParticleConfig;

  constructor(scene: THREE.Scene, config: ParticleConfig) {
    this.config = config;
    const n = config.maxParticles;

    // Allocate typed arrays
    this.posArr   = new Float32Array(n * 3);
    this.alphaArr = new Float32Array(n);

    // Initialize all positions offscreen
    for (let i = 0; i < n; i++) {
      this.posArr[i * 3]     = 0;
      this.posArr[i * 3 + 1] = -9999;
      this.posArr[i * 3 + 2] = 0;
      this.alphaArr[i] = 0;
    }

    // Create buffer geometry
    this.geometry = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.posArr, 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.alphaAttr = new THREE.BufferAttribute(this.alphaArr, 1);
    this.alphaAttr.setUsage(THREE.DynamicDrawUsage);

    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('aAlpha',   this.alphaAttr);

    // Resolve shape (default: circle)
    const shape = config.shape ?? 'circle';

    // Build the #define header so the single fragment shader string
    // can select the correct shape via #ifdef without duplicating
    // ShaderMaterial instances.
    const shapeDefine =
      shape === 'star' ? '#define SHAPE_STAR\n'  :
      shape === 'fire' ? '#define SHAPE_FIRE\n'  :
      /* circle */       '#define SHAPE_CIRCLE\n';

    const baseColor = config.color;
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Vector3(baseColor.r, baseColor.g, baseColor.b) },
        uSize:  { value: config.size },
      },
      vertexShader: /* glsl */ `
        attribute float aAlpha;
        uniform float uSize;
        varying float vAlpha;

        void main() {
          vAlpha = aAlpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = uSize * (200.0 / -mvPosition.z);
          gl_PointSize = clamp(gl_PointSize, 1.0, 48.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: shapeDefine + /* glsl */ `
        precision highp float;
        uniform vec3 uColor;
        varying float vAlpha;

        void main() {

        #ifdef SHAPE_STAR
          // ── 4-point star burst ──
          // Two overlapping diamonds at 0 deg and 45 deg to form an 8-ray star.
          // The 45-deg diamond is slightly narrower for a sharper inner cross.
          vec2 uv = gl_PointCoord - 0.5;

          float diamond1 = 1.0 - (abs(uv.x) + abs(uv.y)) * 2.5;
          // Rotate uv by 45 degrees for the second diamond
          float rotX = uv.x * 0.707 + uv.y * 0.707;
          float rotY = -uv.x * 0.707 + uv.y * 0.707;
          float diamond2 = 1.0 - (abs(rotX) + abs(rotY)) * 3.5;
          float star = max(diamond1, diamond2);
          if (star < 0.0) discard;

          // HDR brightness: core glows extra-bright for bloom
          float alpha = smoothstep(0.0, 0.3, star) * vAlpha;
          gl_FragColor = vec4(uColor * (1.5 + star * 2.0), alpha);

        #elif defined(SHAPE_FIRE)
          // ── Teardrop / flame shape ──
          // Offset the sample point upward so the flame "rises" in the sprite.
          // The shape is wider at the bottom (hot base) and tapers toward the top.
          vec2 uv = gl_PointCoord - vec2(0.5, 0.3);

          // Bias the radius toward the top: positive uv.y means "up", so
          // adding uv.y elongates the shape downward (fatter base) and
          // pinches the top.
          float flameshape = length(uv) + uv.y * 0.4;

          float alpha = smoothstep(0.5, 0.0, flameshape) * vAlpha;
          if (alpha < 0.01) discard;

          // Fire color gradient: white-hot core → orange → transparent edge
          float heat = clamp(1.0 - flameshape * 2.0, 0.0, 1.0);
          vec3 fireColor = mix(vec3(1.0, 0.5, 0.1), vec3(1.0, 1.0, 0.8), heat);

          // HDR brightness on the core for bloom
          gl_FragColor = vec4(fireColor * (1.0 + heat), alpha);

        #else
          // ── Default: soft radial glow (circle) ──
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          float soft = smoothstep(0.5, 0.1, dist);
          gl_FragColor = vec4(uColor * 1.5, soft * vAlpha);
        #endif

        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    scene.add(this.points);

    // CPU pool
    this.particles = new Array(n).fill(null);
  }

  /**
   * Emit `count` particles from world position (x, y, z) in the given direction.
   * Direction does not need to be normalized -- spread is applied around it.
   */
  emit(
    x: number, y: number, z: number,
    dirX: number, dirY: number, dirZ: number,
    count: number
  ): void {
    const { speed, spread, lifetime, maxParticles } = this.config;

    // Normalize direction
    const len = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) || 1;
    const ndx = dirX / len;
    const ndy = dirY / len;
    const ndz = dirZ / len;

    for (let i = 0; i < count; i++) {
      const idx = this.nextIndex;
      this.nextIndex = (this.nextIndex + 1) % maxParticles;

      // Randomize velocity around the emission direction
      const rx = (Math.random() - 0.5) * 2 * spread;
      const ry = (Math.random() - 0.5) * 2 * spread;
      const rz = (Math.random() - 0.5) * 2 * spread;

      const vx = (ndx + rx) * speed;
      const vy = (ndy + ry) * speed;
      const vz = (ndz + rz) * speed;

      // Per-particle lifetime variation (+/- 30%)
      const lt = lifetime * (0.7 + Math.random() * 0.6);

      this.particles[idx] = {
        vx, vy, vz,
        age: 0,
        lifetime: lt,
      };

      const base = idx * 3;
      this.posArr[base]     = x;
      this.posArr[base + 1] = y;
      this.posArr[base + 2] = z;
      this.alphaArr[idx] = 1.0;
    }
  }

  /** Advance all particles by dt seconds. Call once per frame. */
  update(dt: number): void {
    const { gravity, fadeOut, maxParticles } = this.config;

    for (let i = 0; i < maxParticles; i++) {
      const p = this.particles[i];
      if (!p) continue;

      p.age += dt;

      if (p.age >= p.lifetime) {
        // Kill particle
        this.particles[i] = null;
        this.posArr[i * 3 + 1] = -9999;
        this.alphaArr[i] = 0;
        continue;
      }

      // Apply gravity
      p.vy -= gravity * dt;

      // Integrate position
      const base = i * 3;
      this.posArr[base]     += p.vx * dt;
      this.posArr[base + 1] += p.vy * dt;
      this.posArr[base + 2] += p.vz * dt;

      // Clamp to ground
      if (this.posArr[base + 1] < 0.02) {
        this.posArr[base + 1] = 0.02;
        p.vy = 0;
      }

      // Alpha fade
      if (fadeOut) {
        const t = p.age / p.lifetime;
        this.alphaArr[i] = 1.0 - t * t; // quadratic fade
      } else {
        this.alphaArr[i] = 1.0;
      }
    }

    // Flag attributes dirty
    this.posAttr.needsUpdate  = true;
    this.alphaAttr.needsUpdate = true;
  }

  /** Remove the Points mesh from the scene and dispose GPU resources. */
  dispose(): void {
    this.points.parent?.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}
